/**
 * Um apontamento da conferência (E159.F3, GUS-153, ADR-0109 §2.3): o trecho do documento, o que o
 * documento diz × o que a fonte diz, a fonte e para onde a correção vai. O apontamento não se
 * dispensa: a ação é corrigir na fonte e conferir de novo.
 */
import { Link } from 'react-router-dom';

import { ptBR } from '@/i18n/pt-BR';
import {
  destinoDaCorrecao,
  type ApontamentoData,
  type GravidadeDoApontamento,
} from '@/lib/conferenciaDasSaidas';

const t = ptBR.conferenciaDasSaidas;
const ta = t.apontamento;

const COR_DA_GRAVIDADE: Record<GravidadeDoApontamento, string> = {
  bloqueado: 'border-danger-500 bg-danger-50 text-danger-700',
  corrigir: 'border-warning-500 bg-warning-50 text-warning-700',
};

function Linha({ rotulo, texto }: { rotulo: string; texto: string }) {
  return (
    <div className="grid grid-cols-[minmax(0,10rem)_1fr] gap-sc-2 text-body-sm">
      <dt className="text-fg-muted">{rotulo}</dt>
      <dd className="text-fg">{texto}</dd>
    </div>
  );
}

function OndeCorrigir({ apontamento, caseId }: { apontamento: ApontamentoData; caseId: string }) {
  const destino = destinoDaCorrecao(apontamento.onde_corrigir, caseId);
  const texto = ta.ondeCorrigir[apontamento.onde_corrigir];
  if (destino === null) {
    return <p className="text-caption text-fg-muted">{texto}</p>;
  }
  return (
    <Link className="text-body-sm text-accent-700" to={destino}>
      {texto}
    </Link>
  );
}

export function ApontamentoDaSaida({
  apontamento,
  caseId,
}: {
  apontamento: ApontamentoData;
  caseId: string;
}) {
  const titulo = `apontamento-${apontamento.id.slice(0, 12)}`;
  return (
    <article
      className="border-t border-border py-sc-3 first:border-t-0"
      aria-labelledby={titulo}
      data-testid="apontamento"
      data-chave={apontamento.chave}
      data-onde={apontamento.onde_corrigir}
    >
      <div className="flex flex-wrap items-start justify-between gap-sc-2">
        <h4 id={titulo} className="text-body-md font-semibold text-fg">
          {t.criterio[apontamento.chave]}
        </h4>
        <span
          className={`border px-sc-2 py-0.5 text-caption ${COR_DA_GRAVIDADE[apontamento.gravidade]}`}
        >
          {ta.gravidade[apontamento.gravidade]}
        </span>
      </div>
      <blockquote className="mt-sc-2 border-l-2 border-border pl-sc-3 text-body-sm text-fg">
        <span className="sr-only">{ta.trecho}: </span>
        {apontamento.trecho || ta.semTrecho}
      </blockquote>
      <dl className="mt-sc-2 space-y-1">
        <Linha rotulo={ta.noDocumento} texto={apontamento.no_documento} />
        <Linha rotulo={ta.naFonte} texto={apontamento.na_fonte} />
        <Linha rotulo={ta.fonte} texto={apontamento.fonte} />
      </dl>
      <div className="mt-sc-2">
        <OndeCorrigir apontamento={apontamento} caseId={caseId} />
      </div>
    </article>
  );
}
